import React, { useState, useEffect } from 'react'
import { createSelector } from 'reselect'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import {
  Box,
  Container,
  CircularProgress,
  Snackbar,
  IconButton,
  Tab,
  Tabs,
  AppBar,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useTheme } from '@material-ui/core/styles'
import SwipeableViews from 'react-swipeable-views'
import CloseIcon from '@material-ui/icons/Close'
import MetaTags from 'react-meta-tags'
import TagManager from 'react-gtm-module'

//COMPONENTS
import Footer from '../../components/Footer/Footer'
import TabPanel from '../../components/TabPanel/TabPanel'
import ExperienceView from '../ExperiencesView/ExperienceView'
import ContactView from '../ContactView/ContactView'
import AgencyBanner from '../../components/AgencyBanner/AgencyBanner'
//API
import {
  getExperiencesByAgency,
  getAllExperiencesReviews,
} from '../../services/FirebaseServices/experienceServices'
import {
  getAgencyById,
  getAgencyByUrl,
  getSettingsAgency,
} from '../../services/FirebaseServices/agencyServices'

//REDUX
import {
  makeSelectIsExperienceActive,
  makeSelectHasSlug,
  makeSelectAgency,
  set_is_experience_active,
  set_agency,
  set_has_slug,
  clean_experiences,
} from '../../redux/ducks/experiences'
import { getNameCategory, isEmpty } from '../../utils/helpers'

import './styles.scss'
import GoogleReviews from '../ReviewsView/GoogleReviews'

const stateSelector = createSelector(
  [makeSelectIsExperienceActive, makeSelectHasSlug, makeSelectAgency],
  (isExperienceActive, hasSlug, agency) => ({
    isExperienceActive,
    hasSlug,
    agency,
  }),
)
const actionDispatch = (dispatch) => ({
  setIsExperienceIsActive: (isExperienceActive) =>
    dispatch(set_is_experience_active(isExperienceActive)),
  setHasSlug: (hasSlug) => dispatch(set_has_slug(hasSlug)),
  cleanExperiences: () => dispatch(clean_experiences()),
  setAgency: (agency) => dispatch(set_agency(agency)),
})

const CompanyProfile = () => {
  const history = useHistory()
  const theme = useTheme()
  const { agencyId } = useParams()

  //COMPONENT STATE
  const [experiencesData, setExperiencesData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [tabValue, setTabValue] = useState(0)
  const [snackBarOpen, setSnackBarOpen] = useState({
    isOpen: false,
    message: '',
    type: 'warning',
  })
  const [reviews, setReviews] = useState([])

  //REDUX STATE
  let { isExperienceActive, hasSlug, agency } = useSelector(stateSelector)
  const { setIsExperienceIsActive, setHasSlug, cleanExperiences, setAgency } =
    actionDispatch(useDispatch())

  useEffect(() => {
    fetchData()
    return function cleanup() {
      cleanExperiences()
    }
  }, [])

  const fetchData = async () => {
    try {
      const agencyResponse = await getAgencyById(agencyId)
      if (agencyResponse) {
        const experiencesResponse = await getExperiencesByAgency(
          agencyId,
          agencyResponse.name,
        )
        const filterDisableExperiences = experiencesResponse.filter(
          (item) => !item.isDisable,
        )
        const reviewsResponse = await getAllExperiencesReviews(
          agencyResponse.key,
        )
        // SET TITLE
        document.title = `${capitalize(agencyResponse.name)} - ${
          !!experiencesResponse.locator
            ? getNameLocation(experiencesResponse.locator)
            : 'Chile'
        } - ${
          filterDisableExperiences.length > 0
            ? filterDisableExperiences[0].name
            : 'Sin Experiencias'
        }`

        if (!!agencyResponse.gtm) {
          TagManager.initialize({ gmtId: agencyResponse.gtm })
        }

        setAgency(agencyResponse)
        setExperiencesData(filterDisableExperiences)
        setReviews(reviewsResponse)
        setIsLoading(false)
      } else {
        const agencyUrlReponse = await getAgencyByUrl(agencyId)

        const experiencesResponse = await getExperiencesByAgency(
          agencyUrlReponse.key,
          agencyUrlReponse.name,
        )
        const filterDisableExperiences = experiencesResponse.filter(
          (item) => !item.isDisable,
        )

        const reviewsResponse = await getAllExperiencesReviews(
          agencyUrlReponse.key,
        )

        // SET TITLE
        document.title = `${capitalize(agencyUrlReponse.name)} - ${
          !!experiencesResponse.locator
            ? getNameLocation(agencyUrlReponse.locator)
            : 'Chile'
        } - ${
          filterDisableExperiences.length > 0
            ? filterDisableExperiences[0].name
            : 'Sin Experiencias'
        }`

        if (!!agencyUrlReponse.gtm) {
          TagManager.initialize({ gmtId: agencyUrlReponse.gtm })
        }

        setHasSlug(true)
        setAgency(agencyUrlReponse)
        setExperiencesData(filterDisableExperiences)
        setReviews(reviewsResponse)
        setIsLoading(false)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const capitalize = (str) => {
    const lower = str.toLowerCase()
    return str.charAt(0).toUpperCase() + lower.slice(1)
  }

  const getNameLocation = (locator) => {
    if (!!locator) {
      const address = locator.address_components
      if (!!address) {
        const filtered_region = address.filter(function (address_component) {
          return address_component.types.includes('administrative_area_level_1')
        })
        const region = filtered_region.length
          ? filtered_region[0].long_name
          : ''
        const filtered_comuna = address.filter(function (address_component) {
          return address_component.types.includes('administrative_area_level_3')
        })
        const comuna = filtered_comuna.length
          ? filtered_comuna[0].long_name
          : ''
        agency.name = `${agency.name} - ${comuna} - ${region}`
        return `${comuna}, ${region}`
      } else if (!!locator.adr_address) {
        const split = locator.adr_address.split(',')
        const regex = /(<([^>]+)>)/gi
        const comuna = split[1].replace(regex, '')
        const region = split[2].replace(regex, '')
        return `${comuna}, ${region}`
      } else {
        return `${locator.vicinity}`
      }
    } else {
      return ''
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleChangeIndex = (index) => {
    setTabValue(index)
  }

  const getLogo = (agency) => {
    if (agency.logo) {
      return agency.logo
    }
    if (!!agency.settings) {
      if (!!agency.settings.logo.url) {
        return agency.settings.logo.url
      }
      if (!!agency.settings.logo) {
        return agency.settings.logo
      }
    }
    // agency.settings.logo ? agency.settings.logo : agency.logo
  }
  function a11yProps(index) {
    return {
      id: `tab-${index}`,
      'aria-controls': `tabpanel-${index}`,
    }
  }

  return (
    <>
      <MetaTags>
        {!!agency.gsv ? (
          <meta name="google-site-verification" content={agency.gsv} />
        ) : (
          ''
        )}
      </MetaTags>
      <Box>
        {isLoading ? (
          <Container>
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          </Container>
        ) : (
          <>
            <Box
              marginX={
                agency.settings
                  ? agency.settings.bannerModel === 1
                    ? 3
                    : 0
                  : 3
              }
            >
              <AppBar position="static" color="primary">
                <Tabs centered value={tabValue} onChange={handleTabChange}>
                  <Tab label="Experiencias" {...a11yProps(0)} />
                  <Tab label="Contacto" {...a11yProps(1)} />
                  <Tab label="Reseñas" {...a11yProps(2)} />
                </Tabs>
              </AppBar>
            </Box>
            <AgencyBanner
              name={agency.name}
              agencyKey={agency.key}
              logo={getLogo(agency)}
              history={history}
              bannerImage={
                agency.settings && agency.settings.banner
                  ? agency.settings.banner.url
                  : ''
              }
              bannerModel={1}
              isMock={false}
              hide
            />
            <SwipeableViews
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={tabValue}
              onChangeIndex={handleChangeIndex}
              style={{ paddingTop: 0 }}
              slideStyle={{ overflow: 'hidden' }}
            >
              <TabPanel value={tabValue} index={0} dir={theme.direction}>
                <ExperienceView
                  agency={agency}
                  experiencesData={experiencesData}
                />
              </TabPanel>
              <TabPanel value={tabValue} index={1} dir={theme.direction}>
                <ContactView
                  agency={agency}
                  setSnackBarOpen={setSnackBarOpen}
                  handleTabChange={handleTabChange}
                />
              </TabPanel>
              <TabPanel value={tabValue} index={2} dir={theme.direction}>
                {agency?.locator?.place_id &&
                agency?.locator?.place_id !== '' ? (
                  <GoogleReviews place_id={agency.locator.place_id} />
                ) : (
                  <Box my={3} textAlign="center">
                    <p style={{ marginBottom: '0' }}>
                      Actualmente no hay reseñas realizadas
                    </p>
                  </Box>
                )}
              </TabPanel>
            </SwipeableViews>
          </>
        )}
      </Box>
      <Snackbar
        open={snackBarOpen.isOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackBarOpen({ isOpen: false, message: '', type: 'warning' })
        }
      >
        <Alert severity={snackBarOpen.type}>{snackBarOpen.message}</Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={!isExperienceActive}
        autoHideDuration={6000}
        onClose={() => setIsExperienceIsActive(!isExperienceActive)}
        action={
          <React.Fragment>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setIsExperienceIsActive(!isExperienceActive)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      >
        <Alert severity="error">
          La experiencia a la que intento ingresar no se encuentra disponible.
        </Alert>
      </Snackbar>
      <Footer />
    </>
  )
}

export default CompanyProfile
